import { collapseMiniMonthTitle, expandMiniMonthTitle } from './DayPickerRenderer.locstring.json';
import { DateRangeType } from '@fluentui/date-time-utilities/lib/dateValues/dateValues';
import { IconButton } from '@fluentui/react/lib/Button';
import { observer } from 'mobx-react-lite';
import { logUsage } from 'owa-analytics';
import { goToDate, refreshDayPicker } from 'owa-calendar-module-commanding';
import { isBoardViewTypeSelected } from 'owa-calendar-module-store';
import { getNumDaysInDayRange } from 'owa-calendar-surface-options-utils';
import { getSurfaceStore } from 'owa-calendar-surface-surface-store';
import { ControlIcons } from 'owa-control-icons';
import { ICalendarDayProps, ICalendarMonthProps, InlineCalendar } from 'owa-datepicker';
import type { OwaDate } from 'owa-datetime';
import loc from 'owa-localize';
import { useOrchestrator } from 'owa-react-hooks/lib/useOrchestrator';
import { getPalette } from 'owa-theme';
import * as React from 'react';

// TODO: VSO #88707 Separate module vs surface concerns in Calendar module
// Each view type should expose a handler for conditional registration in this file

const styles = require('./DayPickerRenderer.scss');

export default observer(function DayPickerRenderer() {
    const [isExpanded, setIsExpanded] = React.useState<boolean>(true);
    const [key, setKey] = React.useState<boolean>(false);

    /**
     * refreshMiniMonth orchestrator is used to force the component to re-render as needed,
     * e.g. when the 'Today' button is pressed from the calendar command bar.
     *
     * Previously, there was no unique key to pass from the surface to the mini calendar, causing the today button to not reset the mini calendar
     * if no new date was selected. Changing the key with an arbitrary value forces the component to re-render and return to its original
     * state when the today button is pressed.
     *
     * More info on updating uncontrolled components via key:
     * https://reactjs.org/blog/2018/06/07/you-probably-dont-need-derived-state.html#recommendation-fully-uncontrolled-component-with-a-key
     */
    useOrchestrator(
        refreshDayPicker,
        () => {
            setKey(!key);
        },
        [key]
    );

    const renderShowMoreOrLessButton = (): JSX.Element => {
        const text = isExpanded ? loc(collapseMiniMonthTitle) : loc(expandMiniMonthTitle);
        const collapseIcon = isExpanded ? ControlIcons.ChevronDown : ControlIcons.ChevronRight;
        return (
            <IconButton
                className={styles.expandButton}
                title={text}
                ariaLabel={text}
                aria-expanded={isExpanded}
                iconProps={{
                    iconName: collapseIcon,
                    styles: {
                        root: {
                            fontSize: '14px',
                        },
                    },
                }}
                onClick={onShowMoreOrLessButtonClick}>
                {text}
            </IconButton>
        );
    };

    /**
     * if the header is expanded, we let clicks go through to the mini month. If collapsed though, we want a click to re-expand it.
     */
    const onDayPickerWrapperClicked = () => {
        if (!isExpanded) {
            onShowMoreOrLessButtonClick();
        }
    };

    const onShowMoreOrLessButtonClick = () => {
        logUsage('ToggleMiniMonthExpansion', { isExpanded: !isExpanded });
        setIsExpanded(!isExpanded);
        setKey(!key);
    };

    const onDayPickerDateSelected = (selectedDate: OwaDate) => {
        if (!isExpanded) {
            return;
        }

        goToDate(selectedDate);

        // Log Surface Calendar selection
        logUsage('SurfaceCalSelectDate');
    };

    const surfaceStore = getSurfaceStore();
    const weekNumbersShown = surfaceStore.userOptions.showWeekNumbers;
    const currentDate = surfaceStore.selectedDate;
    const palette = getPalette();
    // custom styles to shift the header content and make room for showMoreOrLess button
    const calendarDayProps: Partial<ICalendarDayProps> = {
        daysToSelectInDayView: getNumDaysInDayRange(),
        styles: {
            header: {
                marginLeft: 5,
            },
            monthAndYear: {
                display: 'inline-block',
                textAlign: 'left',
                marginLeft: 20,
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                lineHeight: 28,
                padding: '0 0 0 10px',
            },
            table: {
                marginLeft: 5,
            },
            ...(!isExpanded && {
                monthComponents: {
                    display: 'none',
                },
                table: {
                    display: 'none',
                },
            }),
            ...(weekNumbersShown && {
                root: {
                    fontSize: 12,
                    paddingLeft: 4,
                },
                monthAndYear: {
                    marginLeft: 28,
                },
                weekNumberCell: {
                    backgroundColor: palette.neutralLight,
                    borderColor: palette.neutralTertiaryAlt,
                },
            }),
        },
    };
    const calendarMonthProps: Partial<ICalendarMonthProps> = {
        yearPickerHidden: true, // don't show the year picker in the mini-month
        styles: {
            root: {
                marginLeft: 5,
            },
            headerContainer: {
                marginLeft: 20,
                height: 28,
            },
            ...(!isExpanded && {
                navigationButtonsContainer: {
                    display: 'none',
                },
                gridContainer: {
                    display: 'none',
                },
            }),
        },
    };
    return (
        <div className={styles.dayPickerWrapper} onClick={onDayPickerWrapperClicked}>
            {renderShowMoreOrLessButton()}
            <InlineCalendar
                key={key.toString()}
                value={currentDate}
                showGoToToday={false}
                dateRangeType={calculateMiniMonthDateRangeType()}
                onSelectDate={onDayPickerDateSelected}
                showMonthPickerAsOverlay={isExpanded}
                isMonthPickerVisible={false}
                highlightCurrentMonth={false}
                highlightSelectedMonth={true}
                firstDayOfWeek={surfaceStore.userOptions.weekStartDay}
                showWeekNumbers={weekNumbersShown}
                firstWeekOfYear={surfaceStore.userOptions.firstWeekOfYear}
                workWeekDays={surfaceStore.userOptions.workDays}
                calendarDayProps={calendarDayProps}
                calendarMonthProps={calendarMonthProps}
            />
        </div>
    );
});

function calculateMiniMonthDateRangeType(): DateRangeType {
    let dateRangeType = getSurfaceStore().dateRangeType;
    // When Month or Board view is active, Mini-Month should allow single day selection, emulating Day view's behavior
    if (isBoardViewTypeSelected() || dateRangeType === DateRangeType.Month) {
        dateRangeType = DateRangeType.Day;
    }
    return dateRangeType;
}
